import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';

import { CodeTypeModel }                from '../core/models/code-type';
import { CompanyListsActionsComponent } from './company-lists.actions.component';
import { CompanyListsService }          from './company-lists.service';
import { CompanyService }               from '../companies/company.service';
import { ConfirmService }               from '../messages/confirm/confirm.service';
import { MessagesService }              from '../messages/messages.service';
import { ProductAccountsModel }         from './typings/product-accounts';
import { ProductCategoryModel }         from './typings/product-category';
import { ProductGroupModel }            from './typings/product-group';
import { RemoveComponent }              from '../table/cell-components/actions/remove.component';
import { Table }                        from '../table/types/table';
import { TableColumn }                  from '../table/types/table-column';
import { TableSelectControlComponent }  from '../table/cell-components/controls/select-control/table-select-control.component';
import { TableTextControlComponent }    from '../table/cell-components/controls/text-control/table-text-control.component';

@Component({
  selector   : 'wor-company-lists-product-groups',
  template: require('./company-lists.product-groups.component.html')
})
export class CompanyListsProductGroupsComponent implements OnInit {
  @Input() productGroups: Array<ProductGroupModel> = [];

  accounts          : Array<CodeTypeModel>           = [];
  categoryOptions   : Array<CodeTypeModel>           = [];
  originalGroups    : Map<string, ProductGroupModel> = new Map();
  productCategories : Array<ProductCategoryModel>    = [];
  purchaseAccounts  : Array<CodeTypeModel>           = [];
  tableSettings     : Table                          = {};

  collapsed       = false;
  loadingAccounts = true;

  constructor (
    private companyListsService: CompanyListsService,
    public  companyService     : CompanyService,
    private confirmService     : ConfirmService,
    private messagesService    : MessagesService,
    private translateService   : TranslateService
  ) {}

  ngOnInit () : void {
    this.companyService.getAllAccounts()
    .then(( accounts : ProductAccountsModel ) => {
      this.accounts         = accounts?.sale     || [];
      this.purchaseAccounts = accounts?.purchase || [];
    })
    .then(() => this.companyListsService.getProductCategories(true))
    .then(( productCategories : Array<ProductCategoryModel> ) => {
      this.productCategories = productCategories;

      this.categoryOptions = this.productCategories.map(category => ({
        code       : category.id === null ? 'NONE' : category.id,
        description: category.description
      }));
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => {
      this.loadingAccounts = false;
      this.tableSettings   = this.getTableSettings();

      this.refreshTable();
    });
  }

  addProductGroup () : void {
    this.productGroups = [
      ...this.productGroups,
      {
        account            : '',
        description        : '',
        editing            : true,
        focusField         : 'name',
        name               : '',
        product_category_id: '',
        purchase_account   : ''
      }
    ];
  }

  clearFocusField ( group : ProductGroupModel, field : 'name' | 'description' ) : void {
    if ( group?.focusField === field ) {
      group.focusField = undefined;
    }
  }

  deleteProductGroup ( group : ProductGroupModel ) : void {
    if ( !group.created_on ) {
      this.productGroups = this.productGroups.filter(productGroup => productGroup !== group);

      return;
    }

    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_PRODUCT_GROUP'))
    .then(() => this.companyListsService.deleteProductGroup(group))
    .then(() => {
      this.productGroups = this.productGroups.filter(productGroup => productGroup.name !== group.name);

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.PRODUCT_GROUP'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  getTableSettings () : Table {
    const columns : Array<TableColumn> = [
      {
        component: {
          bindings: {
            outputs: {
              onRemoveClick: ( group : ProductGroupModel ) => this.deleteProductGroup(group)
            }
          },
          enabled: true,
          factory: RemoveComponent
        },
        sortable: false
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( group : ProductGroupModel ) => !group.editing || !!group.created_on,
              giveFocus: ( group : ProductGroupModel ) => group.focusField === 'name',
              model    : 'name',
              name     : 'product_group_name',
              required : true
            },
            outputs: {
              focus: ( group : ProductGroupModel ) => this.clearFocusField(group, 'name')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'name',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.NAME')
        }
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( group : ProductGroupModel ) => !group.editing,
              giveFocus: ( group : ProductGroupModel ) => group.focusField === 'description',
              model    : 'description',
              name     : 'product_group_description'
            },
            outputs: {
              focus: ( group : ProductGroupModel ) => this.clearFocusField(group, 'description')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      }
    ];

    if ( this.companyService.isSubscriptionPlan('gold', false) ) {
      columns.push(this.selectColumn('product_category_id', 'product_group_category', this.categoryOptions, 'GENERAL_SPACE.FIELD.CATEGORY'));
    }

    if ( this.companyService.hasActiveAccountingIntegration() ) {
      columns.push(this.selectColumn('account', 'product_group_account', this.accounts, 'COMPANY_SPACE.INTEGRATIONS.ACCOUNT'));
    }

    if ( this.companyService.hasPurchaseAccounts() ) {
      columns.push(this.selectColumn('purchase_account', 'product_group_purchase_account', this.purchaseAccounts, 'COMPANY_SPACE.INTEGRATIONS.PURCHASE_ACCOUNT'));
    }

    columns.push({
      classes  : 'text-right',
      component: {
        bindings: {
          outputs: {
            onCancelClick: ( group : ProductGroupModel ) => this.toggleEditing(group, false),
            onEditClick  : ( group : ProductGroupModel ) => this.toggleEditing(group, true),
            onSaveClick  : ( group : ProductGroupModel ) => this.save(group)
          }
        },
        enabled: true,
        factory: CompanyListsActionsComponent
      },
      sortable: false
    });

    return {
      columns,
      empty: {
        text: this.translateService.instant('COMPANY_SPACE.LISTS.GRID.NO_PRODUCT_GROUPS')
      },
      pagination: {
        enabled: false
      },
      rows: {
        highlight: {
          enabled: false
        }
      },
      sorting: {
        enabled: false
      }
    };
  }

  refreshTable () : void {
    this.productGroups = this.productGroups.map(group => ({
        ...group,
        product_category_id: !group.product_category_id
          ? 'NONE'
          :  group.product_category_id
      }));
  }

  save ( group : ProductGroupModel ) : void {
    if ( !group.name ) {
      this.messagesService.error(this.translateService.instant('JS_SPACE.MESSAGES.INVALID_INPUT'));

      return;
    }

    if ( group.product_category_id === 'NONE' ) {
      group.product_category_id = '';
    }

    this.companyListsService.saveProductGroup(group)
    .then(( savedGroup : ProductGroupModel ) => {
      Object.assign(group, savedGroup, {
        editing   : false,
        focusField: undefined
      });

      this.originalGroups.delete(group.name);

      this.refreshTable();

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.PRODUCT_GROUP'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  selectColumn ( model : string, name : string, options : Array<CodeTypeModel>, header : string ) : TableColumn {
    return {
      component: {
        bindings: {
          inputs: {
            disabled: ( group : ProductGroupModel ) => !group.editing || this.loadingAccounts,
            model,
            name,
            options
          }
        },
        enabled: true,
        factory: TableSelectControlComponent
      },
      header: {
        text: this.translateService.instant(header)
      }
    };
  }

  toggleEditing ( group : ProductGroupModel, editing : boolean ) : void {
    if ( editing ) {
      if ( group.created_on && !this.originalGroups.has(group.name) ) {
        this.originalGroups.set(group.name, { ...group, editing: false });
      }

      group.editing    = true;
      group.focusField = 'description';
    }
    else if ( !group.created_on ) {
      this.deleteProductGroup(group);

      return;
    }
    else {
      const originalGroup = this.originalGroups.get(group.name);

      if ( originalGroup ) {
        Object.assign(group, originalGroup);
        this.originalGroups.delete(group.name);
      }

      group.editing    = false;
      group.focusField = undefined;
    }

    this.refreshTable();
  }
}
